import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Loader2, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// le prenotazioni arrivano subito su Telegram tramite Jarvis
const LEAD_URL = "https://agente-studio-production.up.railway.app/lead";
const TIMES = [
  "12:00", "12:30", "13:00", "13:30", "14:00",
  "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30",
];

export default function BookingForm() {
  const { t } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    data: "",
    ora: "19:00",
    ospiti: "2",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefono.trim() || !form.data) {
      toast.error(f.missing);
      return;
    }
    setLoading(true);
    try {
      const giorno = new Date(form.data + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
      await axios.post(LEAD_URL, {
        nome: form.nome,
        contatto: form.telefono,
        messaggio: `Tavolo per ${form.ospiti} ${Number(form.ospiti) === 1 ? "persona" : "persone"} - ${giorno} (${form.data}) alle ${form.ora}${form.note.trim() ? ` - Note: ${form.note.trim()}` : ""}`,
        pagina: window.location.href,
        sito_web: "",
      });
      toast.success(f.success, { duration: 6000 });
      setForm({ nome: "", telefono: "", data: "", ora: "19:00", ospiti: "2", note: "" });
    } catch (err) {
      toast.error(f.error, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="bk-nome">{f.name}</label>
          <input
            id="bk-nome"
            data-testid="booking-name-input"
            className="field"
            placeholder={f.namePh}
            value={form.nome}
            onChange={set("nome")}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="bk-telefono">{f.phone}</label>
          <input
            id="bk-telefono"
            data-testid="booking-phone-input"
            type="tel"
            className="field"
            placeholder={f.phonePh}
            value={form.telefono}
            onChange={set("telefono")}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="bk-data">{f.date}</label>
          <input
            id="bk-data"
            data-testid="booking-date-input"
            type="date"
            min={today}
            className="field [color-scheme:dark]"
            value={form.data}
            onChange={set("data")}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="bk-ora">{f.time}</label>
          <div className="relative">
            <select
              id="bk-ora"
              data-testid="booking-time-select"
              className="field appearance-none pr-10"
              value={form.ora}
              onChange={set("ora")}
            >
              {TIMES.map((h) => (
                <option key={h} value={h} className="bg-ink">{h}</option>
              ))}
            </select>
            <ChevronDown size={15} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gold" />
          </div>
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="bk-ospiti">{f.guests}</label>
        <div className="relative">
          <select
            id="bk-ospiti"
            data-testid="booking-guests-select"
            className="field appearance-none pr-10"
            value={form.ospiti}
            onChange={set("ospiti")}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n} className="bg-ink">
                {n} {f.guestsLabel}
              </option>
            ))}
          </select>
          <ChevronDown size={15} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gold" />
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="bk-note">{f.notes}</label>
        <textarea
          id="bk-note"
          data-testid="booking-notes-input"
          rows={3}
          className="field resize-none"
          placeholder={f.notesPh}
          value={form.note}
          onChange={set("note")}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="booking-submit-btn"
        className="group inline-flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-black transition-all duration-500 hover:bg-gold-light disabled:opacity-60 sm:w-auto"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            {f.sending}
          </>
        ) : (
          <>
            <Send size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
            {f.submit}
          </>
        )}
      </button>
    </form>
  );
}
